import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, FileText, AudioLines, Download } from 'lucide-react';
import { saveBlob } from '../judicial/downloadPdf';
import { IntakeUpload } from './IntakeUpload';
import { PendingUpdateReview } from './PendingUpdateReview';
import { StrategyReportButtons } from './StrategyReportButtons';

const RANK = { alta: 0, media: 1, baja: 2 };
const TONE = { alta: 'bg-red-500/20 text-red-300 border-red-500/40', media: 'bg-amber-500/20 text-amber-300 border-amber-500/40', baja: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' };
const TYPE = { resolucion: 'Resolución', sentencia: 'Sentencia', notificacion: 'Notificación', demanda: 'Demanda / escrito', contrato: 'Contrato', otro: 'Otro' };

export const sortByUrgency = (docs = []) => [...docs].sort((a, b) => {
  const ra = RANK[a.classification?.urgency] ?? 3;
  const rb = RANK[b.classification?.urgency] ?? 3;
  return ra - rb || String(b.imported_at || '').localeCompare(String(a.imported_at || ''));
});

export const MatterDocumentsDialog = ({ matter, open, onOpenChange, onUpdated }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const docs = sortByUrgency(matter.documents);
  const pending = docs.filter((d) => !d.classification?.ai_classified).length;
  const [upd, setUpd] = useState(matter.pending_update || null);
  const [round, setRound] = useState(0);
  const download = async (d) => {
    try { const r = await api.get(`/crm/matters/${matter.id}/files/${d.file_id}`, { responseType: 'blob' }); saveBlob(r.data, d.filename); }
    catch { toast.error('No se pudo descargar el documento'); }
  };

  useEffect(() => {
    if (!pending) return;
    setBusy(true);
    api.post(`/crm/matters/${matter.id}/documents/classify`)
      .then((r) => { if (r.data.classified) onUpdated(); if (r.data.pending) toast.error(`${r.data.pending} documento(s) no se pudieron clasificar`); })
      .catch(() => toast.error('No se pudieron clasificar los documentos'))
      .finally(() => setBusy(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="matter-documents-dialog" className="bg-slate-950 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Documentos por urgencia</DialogTitle>
          <DialogDescription className="text-slate-400">{matter.title} · ordenados de urgencia alta a baja</DialogDescription>
        </DialogHeader>
        {busy && <p data-testid="matter-documents-classifying" className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Clasificando documentos anteriores...</p>}
        {matter.intake_strategy && !upd && <StrategyReportButtons matterId={matter.id} title={matter.title} testid="docs-strategy-report" />}
        {!upd && <IntakeUpload key={round} testid="matter-docs" endpoint={`/crm/matters/${matter.id}/documents/upload-analyze`} label="Cargar documentos nuevos al expediente"
          cta="Guardar y analizar: proponer cambios y nueva estrategia" onResult={(r) => { setUpd(r.pending_update); onUpdated(); }} />}
        {upd && <PendingUpdateReview matter={matter} update={upd} onDone={() => { setUpd(null); setRound((x) => x + 1); onUpdated(); }} />}
        {docs.length === 0 && <p className="text-sm text-slate-500">Este caso aún no tiene documentos importados.</p>}
        <ul data-testid="matter-documents-list" className="space-y-2">
          {docs.map((d, i) => {
            const c = d.classification || {};
            return (
              <li key={`${d.filename}-${i}`} data-testid={`matter-document-${i}`} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
                    {d.source === 'audio' ? <AudioLines className="w-4 h-4 text-cyan-400 shrink-0" /> : <FileText className="w-4 h-4 text-slate-400 shrink-0" />}
                    <span className="truncate">{d.filename}</span>
                  </p>
                  <div className="flex gap-1.5 shrink-0">
                    {d.file_id && <button type="button" data-testid={`matter-document-download-${i}`} onClick={() => download(d)} className="text-[11px] border border-slate-600 rounded-full px-2 py-0.5 text-slate-300 hover:border-amber-500 flex items-center gap-1"><Download className="w-3 h-3" />Original</button>}
                    {c.type && <span className="text-[11px] border border-slate-600 rounded-full px-2 py-0.5 text-slate-300">{TYPE[c.type] || c.type}</span>}
                    <span data-testid={`matter-document-urgency-${i}`} className={`text-[11px] border rounded-full px-2 py-0.5 ${TONE[c.urgency] || 'border-slate-700 text-slate-500'}`}>
                      {c.urgency ? `Urgencia ${c.urgency}` : 'Sin clasificar'}
                    </span>
                  </div>
                </div>
                {d.resumen && <p className="text-xs text-slate-400 mt-1.5 line-clamp-3">{d.resumen}</p>}
                <p className="text-[11px] text-slate-500 mt-1">{d.source === 'carga_inicial' ? 'Cargado al crear el caso · ' : d.source === 'carga_posterior' ? 'Cargado después de crear el caso · ' : ''}{d.read_method === 'lectura_ia' ? 'escaneado, leído con IA · ' : ''}Importado {String(d.imported_at || '').slice(0, 10).split('-').reverse().join('-')}</p>
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
